// ResponsiveImage — lazy-loaded image with srcset for Unsplash + Amazon URLs.
// Auto-resizes Amazon m.media-amazon.com images to a smaller catalog size to
// keep individual image payloads under ~100KB (Amazon's default variant is
// often 300–500KB per photo, which is what SEO auditors flag).
//
// Usage: <ResponsiveImage src={url} alt={alt} ... />
// Defaults: loading=lazy, decoding=async, eager only when `eager` prop is true.

const UNSPLASH_HOST = "images.unsplash.com";
const SRCSET_WIDTHS = [400, 600, 900, 1200, 1600];
const AMAZON_HOSTS = ["m.media-amazon.com", "images-na.ssl-images-amazon.com"];

// Rewrite an Unsplash URL to a smaller, JPEG-optimized variant. Used for
// the non-srcset fallback `src` attribute (many auditors + LLM crawlers
// ignore srcset and grab the raw src, so we downsize that too) and for
// og:image meta tags. Callers can request a larger `w` for hero/OG use.
function shrinkUnsplashUrl(rawUrl, width = 800, quality = 60) {
    try {
        const u = new URL(rawUrl);
        if (u.host !== UNSPLASH_HOST) return rawUrl;
        u.searchParams.set("auto", "format");
        u.searchParams.set("fit", "crop");
        u.searchParams.set("w", String(width));
        u.searchParams.set("q", String(quality));
        return u.toString();
    } catch {
        return rawUrl;
    }
}

function buildUnsplashSrcset(rawUrl) {
    try {
        const u = new URL(rawUrl);
        if (u.host !== UNSPLASH_HOST) return null;
        u.searchParams.delete("w");
        u.searchParams.set("auto", "format");
        u.searchParams.set("fit", "crop");
        // Force q=60 (was 70+) for smaller filesize — negligible visual hit
        // for thumbnails but ~30% smaller payload.
        u.searchParams.set("q", "60");
        return SRCSET_WIDTHS.map((w) => {
            const variant = new URL(u.toString());
            variant.searchParams.set("w", String(w));
            return `${variant.toString()} ${w}w`;
        }).join(", ");
    } catch {
        return null;
    }
}

// Amazon's image CDN accepts inline size directives:
//   /images/I/71ABC.jpg  →  /images/I/71ABC._SL500_.jpg  (500px longest side)
// Existing directives (_AC_SL1500_, _SL500_, etc.) are stripped so we control
// the final size. Full-size photos drop from ~350KB to ~30–50KB.
function shrinkAmazonUrl(rawUrl, size = 500) {
    try {
        const u = new URL(rawUrl);
        if (!AMAZON_HOSTS.includes(u.host)) return rawUrl;
        // Legacy `/images/P/{ASIN}.01.LZZZZZZZ.jpg` URLs must be left ALONE.
        // Amazon does NOT honor the `_SL` directive on that endpoint — adding
        // one downgrades the image to a 135×135 blurry thumbnail. Those
        // variants are already ~13–31KB so there's no size violation to fix.
        if (/^\/images\/P\/[A-Z0-9]{10}\.\d+\.[A-Z]+\.[a-z]+$/i.test(u.pathname)) {
            return rawUrl;
        }
        // Standard `/images/I/{id}.jpg` (and its `._AC_SL1500_` variants).
        // Strip any existing directives before appending our own so we don't
        // stack them.
        const m = u.pathname.match(/^(.*\/[^/.]+?)(?:\._[A-Z][A-Z0-9_]+_)*(\.[a-z]+)$/i);
        if (!m) return rawUrl;
        u.pathname = `${m[1]}._SL${size}_${m[2]}`;
        return u.toString();
    } catch {
        return rawUrl;
    }
}

function buildAmazonSrcset(rawUrl) {
    if (!AMAZON_HOSTS.some((h) => rawUrl.includes(h))) return null;
    return [300, 500, 800]
        .map((w) => `${shrinkAmazonUrl(rawUrl, w)} ${w}w`)
        .join(", ");
}

export default function ResponsiveImage({
    src,
    alt,
    className,
    sizes = "(min-width: 1024px) 50vw, 100vw",
    eager = false,
    fetchpriority,
    width = 800,
    height = 500,
    ...rest
}) {
    let normalizedSrc = src;
    let srcSet = null;
    if (src) {
        srcSet = buildUnsplashSrcset(src);
        if (srcSet) {
            // Shrink the base src for browsers/crawlers that ignore srcset.
            // Auditors typically read raw src; keeping this small (~40KB
            // for 800×q60) trims their "image too large" flags.
            normalizedSrc = shrinkUnsplashUrl(src, 800, 60);
        } else if (AMAZON_HOSTS.some((h) => src.includes(h))) {
            normalizedSrc = shrinkAmazonUrl(src, 500);
            srcSet = buildAmazonSrcset(src);
        }
    }
    return (
        <img
            src={normalizedSrc}
            alt={alt}
            className={className}
            loading={eager ? "eager" : "lazy"}
            decoding="async"
            width={width}
            height={height}
            {...(fetchpriority ? { fetchPriority: fetchpriority } : {})}
            {...(srcSet ? { srcSet, sizes } : {})}
            {...rest}
        />
    );
}
